import {
  useRef,
  useCallback,
  useMemo,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import {createPortal} from 'react-dom';
import {
  PortalPopoutManagerContext,
  buildWindowFeatures,
  DEFAULT_POPOUT_OPTIONS,
  type PopoutInstance,
  type PopoutOptions,
  type PortalPopoutManagerProps,
} from './PortalPopoutReduxContext';
import {PopoutWindowProvider} from './PopoutWindowProvider';
import {
  copyStyles,
  copyDynamicStyles,
  copyLocalScssInjectedStyles,
  observeStyles,
} from './utils/copyStyles';

// ----------------------------
// PortalPopoutManager
// ----------------------------
export const PortalPopoutManager = ({
  children,
  defaultOptions = DEFAULT_POPOUT_OPTIONS,
  onPopoutOpen,
  onPopoutClose,
}: PortalPopoutManagerProps): ReactNode => {
  // Track all popout instances
  const [popouts, setPopouts] = useState<Map<string, PopoutInstance>>(
    new Map()
  );
  const popoutsRef = useRef<Record<string, PopoutInstance>>({});

  const [contentRegistry, setContentRegistry] = useState<
    Map<string, ReactNode>
  >(new Map());

  const styleObservers = useRef<Map<string, MutationObserver>>(new Map());
  // ---------------

  const cleanupPopout = useCallback((id: string) => {
    const observer = styleObservers.current.get(id);
    if (observer) {
      observer.disconnect();
      styleObservers.current.delete(id);
    }

    // Remove from popouts map
    setPopouts((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);
  // ---------------

  const openPopout = useCallback(
    (id: string, options?: PopoutOptions) => {
      const mergedOptions = {...defaultOptions, ...options};

      // Check if already open in our state
      const existing = popouts.get(id);
      if (existing && !existing.windowRef.closed) {
        existing.windowRef.focus();
        return;
      }
      const features = buildWindowFeatures(mergedOptions.windowFeatures || {});
      const newWindow = window.open('', `popout-${id}`, features);

      if (!newWindow) {
        console.error(
          `Popout "${id}" blocked by browser. Please allow popups for this site.`
        );
        return;
      }
      // ---------------

      // Check if this is an existing window that needs to be reinitialized
      const existingRoot = newWindow.document.getElementById('popout-root');
      const needsReinit = existingRoot !== null;

      if (needsReinit) {
        newWindow.document.head.innerHTML = '';
        newWindow.document.body.innerHTML = '';
      }

      // Setup document
      newWindow.document.title = mergedOptions.title || 'Popout Window';

      // Add base styles
      const baseStyle = newWindow.document.createElement('style');
      baseStyle.textContent = `
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; width: 100%; height: 100%; }
      #popout-root { width: 100%; min-height: 100vh; }
    `;
      newWindow.document.head.appendChild(baseStyle);

      // Create container
      const container = newWindow.document.createElement('div');
      container.id = 'popout-root';
      newWindow.document.body.appendChild(container);

      // Copy styles from main window
      if (mergedOptions.copyStyles !== false) {
        copyStyles(document, newWindow.document);
        copyDynamicStyles(document, newWindow.document);
        copyLocalScssInjectedStyles(document, newWindow.document);
      }

      // Setup style observer (disconnect any existing observer first)
      const existingObserver = styleObservers.current.get(id);
      if (existingObserver) {
        existingObserver.disconnect();
      }

      if (mergedOptions.observeStyles !== false) {
        const observer = observeStyles(document, newWindow.document);
        styleObservers.current.set(id, observer);
      }

      // Create popout instance
      const instance: PopoutInstance = {
        id,
        windowRef: newWindow,
        containerRef: container,
        title: mergedOptions.title || 'Popout Window',
        isReady: true,
        options: mergedOptions,
      };

      // Handle window close
      newWindow.onbeforeunload = () => {
        cleanupPopout(id);
        onPopoutClose?.(id);
      };

      // Add to state
      setPopouts((prev) => {
        const next = new Map(prev);
        next.set(id, instance);
        return next;
      });

      onPopoutOpen?.(id);
    },
    [defaultOptions, popouts, cleanupPopout, onPopoutOpen, onPopoutClose]
  );
  // ---------------

  //  Close a specific popout
  const closePopout = useCallback(
    (id: string) => {
      const instance = popouts.get(id);
      if (instance && !instance.windowRef.closed) {
        instance.windowRef.close();
      }
      cleanupPopout(id);
      onPopoutClose?.(id);
    },
    [popouts, cleanupPopout, onPopoutClose]
  );
  // ---------------

  // Close all popouts
  const closeAll = useCallback(() => {
    popouts.forEach((instance, id) => {
      if (!instance.windowRef.closed) {
        instance.windowRef.close();
      }
      cleanupPopout(id);
      onPopoutClose?.(id);
    });
  }, [popouts, cleanupPopout, onPopoutClose]);
  // ---------------

  // Get a specific popout instance
  const getPopout = useCallback(
    (id: string) => {
      return popouts.get(id);
    },
    [popouts]
  );
  // ---------------

  // Focus a specific popout window
  const focusPopout = useCallback(
    (id: string) => {
      const instance = popouts.get(id);
      if (instance && !instance.windowRef.closed) {
        instance.windowRef.focus();
      }
    },
    [popouts]
  );
  // ---------------

  // Check if a popout is open
  const isPopoutOpen = useCallback(
    (id: string) => {
      const instance = popouts.get(id);
      return !!instance && !instance.windowRef.closed;
    },
    [popouts]
  );
  // ---------------

  const registerContent = useCallback((id: string, content: ReactNode) => {
    setContentRegistry((prev) => {
      const next = new Map(prev);
      next.set(id, content);
      return next;
    });
  }, []);

  // ---------------

  const unregisterContent = useCallback((id: string) => {
    setContentRegistry((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);
  // ---------------

  //Get registered content for a popout
  const getContent = useCallback(
    (id: string) => {
      return contentRegistry.get(id);
    },
    [contentRegistry]
  );
  // ---------------

  // Periodically check for externally closed windows
  useEffect(() => {
    const checkClosed = setInterval(() => {
      popouts.forEach((instance, id) => {
        if (instance.windowRef.closed) {
          cleanupPopout(id);
          onPopoutClose?.(id);
        }
      });
    }, 500);

    return () => clearInterval(checkClosed);
  }, [popouts, cleanupPopout, onPopoutClose]);
  // ---------------

  // Close all popouts on page beforeunload (refresh/close)
  useEffect(() => {
    // Add popout changes to ref
    popoutsRef.current = Object.fromEntries(popouts);

    const handleBeforeUnload = () => {
      popouts.forEach((instance) => {
        if (!instance.windowRef.closed) {
          instance.windowRef.close();
        }
      });
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [popouts]);
  // ---------------

  // Cleanup all on unmount
  useEffect(() => {
    return () => {
      for (const instance of Object.values(popoutsRef.current)) {
        if (!instance.windowRef.closed) {
          instance.windowRef.close();
        }
      }
      styleObservers.current.forEach((observer) => observer.disconnect());
    };
  }, []);
  // ---------------

  // Context value
  const contextValue = useMemo(
    () => ({
      popouts,
      openPopout,
      closePopout,
      closeAll,
      getPopout,
      focusPopout,
      isPopoutOpen,
      registerContent,
      unregisterContent,
      getContent,
    }),
    [
      popouts,
      openPopout,
      closePopout,
      closeAll,
      getPopout,
      focusPopout,
      isPopoutOpen,
      registerContent,
      unregisterContent,
      getContent,
    ]
  );
  // ---------------

  // Render portals for all open popouts with PopoutWindowProvider
  const portals = useMemo(() => {
    const result: ReactNode[] = [];

    popouts.forEach((instance, id) => {
      const content = contentRegistry.get(id);
      if (content && instance.isReady && !instance.windowRef.closed) {
        result.push(
          createPortal(
            <PopoutWindowProvider
              key={id}
              popoutWindow={instance.windowRef}
              popoutDocument={instance.windowRef.document}
              popoutId={id}
            >
              <div
                className="portal-popout-root"
                style={{width: '100%', minHeight: '100vh'}}
                data-testid={`portal-popout-manager-content-${id}`}
              >
                {content}
              </div>
            </PopoutWindowProvider>,
            instance.containerRef,
            id
          )
        );
      }
    });

    return result;
  }, [popouts, contentRegistry]);
  // ---------------

  return (
    <PortalPopoutManagerContext.Provider value={contextValue}>
      {children}
      {portals}
    </PortalPopoutManagerContext.Provider>
  );
};

export default PortalPopoutManager;
